<template>
  <div class="p-8 bg-[#F8FAFC] min-h-screen">
    <header class="flex lg:flex-row justify-between items-start lg:items-center mb-8 gap-6">
      <div class="flex items-center gap-4">
        <div class="p-3 bg-white rounded-2xl shadow-sm border border-gray-100">
          <img src="https://www.datafuse.fr/assets/logo_datafuse_automation%20(4).svg" class="h-8" alt="Logo" />
        </div>
        <div>
          <h1 class="text-2xl font-black text-gray-900 uppercase tracking-tight">Poste : <span class="text-dimotec-orange">Caisse n°{{ currentStation }}</span></h1>
          <p class="text-sm text-gray-500 font-medium italic">Agent connecté : {{ currentUser }}</p>
        </div>
      </div>
      
      <div class="flex items-center gap-4">
        <div class="text-right hidden md:block">
          <p class="text-[10px] font-black text-gray-400 uppercase">Ma file prioritaire</p>
          <p class="text-sm font-bold text-gray-700">{{ priorityQueue.name }}</p>
        </div>
        <div class="h-12 w-12 bg-blue-500/10 rounded-2xl flex items-center justify-center text-dimotec-orange">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
        </div>
      </div>
    </header>

    <div class="grid xl:grid-cols-3 gap-8">
      
      <div class="xl:col-span-2 space-y-6">
        
        <div v-if="priorityQueue.count === 0 && otherQueuesHaveWaiting" 
             class="bg-blue-50 border-2 border-blue-200 rounded-[2rem] p-6 flex items-center justify-between shadow-lg shadow-blue-100 animate-in fade-in slide-in-from-top-4">
          <div class="flex items-center gap-4">
            <div class="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center text-blue-600 animate-bounce">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="3" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
            </div>
            <div>
              <p class="text-blue-900 font-black uppercase text-xs tracking-widest">Votre file est vide !</p>
              <p class="text-blue-700 text-sm font-medium">Il y a <span class="font-black">{{ totalWaitingOther }}</span> personnes en attente dans les autres files.</p>
            </div>
          </div>
          <button @click="pickFromOtherQueue" class="bg-blue-600 text-white px-6 py-3 rounded-xl font-black text-xs uppercase tracking-tighter hover:bg-blue-700 transition-all shadow-md">
            Aider une autre file
          </button>
        </div>

        <div class="bg-white rounded-[2.5rem] shadow-xl shadow-gray-200/40 p-8 border border-gray-50 relative overflow-hidden">
          <div class="flex justify-between items-center mb-8">
             <div class="flex flex-col">
                <span class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Traitement en cours</span>
                <h2 class="text-xl font-black text-gray-800 uppercase">{{ currentTicket.queueName || priorityQueue.name }}</h2>
             </div>
             <span class="px-4 py-2 bg-gray-900 text-white rounded-xl text-xs font-black tracking-widest uppercase">Ticket #{{ currentTicket.id }}</span>
          </div>

          <div class="flex flex-col items-center justify-center py-10 bg-[#222222] rounded-[2.5rem] mb-8 shadow-inner">
            <p class="text-[10px] font-black text-dimotec-orange uppercase tracking-[0.4em] mb-2">Appelé au guichet</p>
            <div class="text-9xl font-black text-white tabular-nums tracking-tighter">{{ currentTicket.number }}</div>
            <div class="mt-6 flex gap-3">
               <span class="px-3 py-1 bg-white/10 rounded-lg text-[10px] font-bold text-gray-400 uppercase tracking-widest">Attente: {{ currentTicket.waitTime }}min</span>
            </div>
          </div>

          <div class="grid md:grid-cols-4 gap-4">
            <button class="flex flex-col items-center p-6 bg-gray-50 rounded-3xl border border-gray-100 hover:bg-gray-100 transition-all group">
              <svg class="w-6 h-6 mb-2 text-gray-400 group-hover:text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M12.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0019 16V8a1 1 0 00-1.6-.8l-5.334 4zM4.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0011 16V8a1 1 0 00-1.6-.8l-5.334 4z" /></svg>
              <span class="text-[10px] font-black uppercase">Rappeler</span>
            </button>

            <button @click="callNext" class="col-span-2 flex flex-col items-center p-6 bg-blue-500 text-white rounded-3xl shadow-lg shadow-blue-200 hover:scale-[1.02] transition-all">
              <svg class="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="3" d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
              <span class="text-sm font-black uppercase tracking-widest">Suivant</span>
            </button>

            <button class="flex flex-col items-center p-6 bg-gray-50 rounded-3xl border border-gray-100 hover:bg-gray-100 transition-all group">
              <svg class="w-6 h-6 mb-2 text-gray-400 group-hover:text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
              <span class="text-[10px] font-black uppercase">Absent</span>
            </button>
          </div>
        </div>
      </div>

      <div class="space-y-6">
        <div class="bg-white rounded-[2.5rem] p-8 border border-gray-100 shadow-sm">
          <h3 class="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mb-6 text-center">État Global des Files</h3>
          
          <div class="space-y-4">
            <div v-for="queue in allQueues" :key="queue.name" 
                 class="p-4 rounded-2xl border transition-all"
                 :class="queue.name === priorityQueue.name ? 'border-dimotec-orange bg-blue-50/30' : 'border-gray-50 bg-gray-50/50'">
              <div class="flex justify-between items-center">
                <p class="text-xs font-black uppercase tracking-tighter" :class="queue.name === priorityQueue.name ? 'text-dimotec-orange' : 'text-gray-500'">{{ queue.name }}</p>
                <span class="text-lg font-black" :class="queue.count > 10 ? 'text-red-500' : 'text-gray-900'">{{ queue.count }}</span>
              </div>
              <div class="w-full bg-gray-200 h-1.5 rounded-full mt-3 overflow-hidden">
                <div class="h-full bg-current transition-all" :style="{ width: (queue.count / 20 * 100) + '%' }"></div>
              </div>
            </div>
          </div>
        </div>

        <div class="bg-gray-900 rounded-[2.5rem] p-6 text-white">
           <p class="text-[10px] font-black text-dimotec-orange uppercase mb-2">Astuce Datafuse</p>
           <p class="text-xs text-gray-400 leading-relaxed font-medium">Le bouton <span class="text-white">"Aider une autre file"</span> n'apparaît que si votre file prioritaire est vide et que d'autres clients s'impatientent ailleurs.</p>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      currentUser: "Enzo Viana",
      currentStation: 1,
      priorityQueue: { name: "Diagnostic Vente", count: 0 },
      currentTicket: { id: 442, number: "A-042", waitTime: 4, queueName: "Diagnostic Vente" },
      allQueues: [
        { name: "Diagnostic Vente", count: 0 },
        { name: "Diagnostic Location", count: 14 },
        { name: "Autre Demande", count: 3 }
      ]
    }
  },
  computed: {
    totalWaitingOther() {
      return this.allQueues
        .filter(q => q.name !== this.priorityQueue.name)
        .reduce((acc, q) => acc + q.count, 0);
    },
    otherQueuesHaveWaiting() {
      return this.totalWaitingOther > 0;
    }
  },
  methods: {
    pickFromOtherQueue() {
      // Logique pour appeler le premier ticket dispo dans une autre file
      const busyQueue = this.allQueues.find(q => q.count > 0 && q.name !== this.priorityQueue.name);
      if(busyQueue) {
        this.currentTicket = {
          id: Math.floor(Math.random() * 999),
          number: "B-0" + Math.floor(Math.random() * 99),
          waitTime: 12,
          queueName: busyQueue.name
        };
        busyQueue.count--;
        alert("Vous récupérez un client de la file : " + busyQueue.name);
      }
    },
    callNext() {
      if (this.priorityQueue.count > 0) {
        this.priorityQueue.count--;
        // Logic to update ticket
      } else if (this.otherQueuesHaveWaiting) {
         this.pickFromOtherQueue();
      }
    }
  }
}
</script>